import { createServerFn } from '@tanstack/react-start';
import { setCookie } from '@tanstack/react-start/server';

import type { Client } from '@/api/core-api/client';

import { integrationsControllerMarkTaskAsCompleteByProjectCompletionId } from '@/api/core-api';
import { assertClient, getClient } from '@/utils/get-api-client';

import {
  listExternalSessions,
  markExternalSessionCompleted,
} from './external-session';

export type ExternalStudyCompletion =
  /** A session on this browser completed the study, now or earlier. */
  | 'COMPLETED'
  /** No external session on this browser belongs to the study. */
  | 'NONE'
  /** The study's session was found but core-api refused the completion. */
  | 'FAILED';

function tryCompletion(
  client: Client | undefined,
  projectCompletionId: string,
) {
  return integrationsControllerMarkTaskAsCompleteByProjectCompletionId({
    client,
    path: { projectCompletionId },
    throwOnError: false,
  });
}

/** core-api's 409 code for a completion whose task is already complete. */
const TASK_ALREADY_COMPLETED = 'TASK_ALREADY_COMPLETED';
/** core-api's 409 code for an account whose response is no longer invited. */
const COMPLETION_REFUSED = 'COMPLETION_REFUSED';

function conflictCode(error: unknown): string | undefined {
  return typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'string'
    ? error.error
    : undefined;
}

/**
 * Completes an unmoderated study for the external participant on this
 * browser, given only the completion id the survey redirected back with.
 *
 * A survey sends every participant to one project-wide completion URL that
 * names the project's completion id, not the project, while an external
 * participant is signed in per project. So each account the browser holds a
 * session for is tried in turn: core-api scopes a completion to the calling
 * account and the id, so only the account that applied to the study
 * succeeds, and the others answer 404.
 *
 * One contact email is one account across every project, so the account
 * that succeeds may have been read off another project's session. Only the
 * session for the project the completion attended is marked completed: a
 * session marked done turns its own project's landing page into "already
 * completed", so marking the wrong one would lock a still-open application
 * out of its invite link.
 *
 * A 409 names one account's standing on this study, not the study's: either
 * that account's task was already complete (a repeat of this page), or its
 * response is no longer invited (cancelled, say) and core-api refused it. On
 * a shared browser the account that answers either way may not be the one
 * now at the end of the survey, so both keep the walk going. The walk ends
 * done once any account had already completed, and refused only when every
 * account refused. Any other error is about the study itself (it is no
 * longer accepting completions) and stops the walk.
 */
export const completeExternalStudy = createServerFn()
  .validator((data: { projectCompletionId: string }) => data)
  .handler(async ({ data }): Promise<ExternalStudyCompletion> => {
    const sessions = await listExternalSessions();
    const tried = new Set<string>();
    let alreadyCompleted = false;
    let refused = false;

    for (const { session } of sessions) {
      if (tried.has(session.userId)) {
        continue;
      }
      tried.add(session.userId);

      const client = getClient(session.userId);
      assertClient(client);

      // Sequential on purpose: at most one account can own the completion,
      // and trying them in parallel would send the same completion as
      // several accounts at once.
      // eslint-disable-next-line no-await-in-loop
      const response = await tryCompletion(client, data.projectCompletionId);

      if (response.status === 404) {
        continue;
      }

      if (response.data) {
        // The account that just completed, on the project the completion
        // attended: another account on this browser may hold a session for
        // the same project, and marking that one would lock it out.
        const attended = sessions.find(
          (entry) =>
            entry.session.userId === session.userId &&
            entry.session.projectId.toLowerCase() ===
              String(response.data.projectId).toLowerCase(),
        );
        if (attended) {
          // eslint-disable-next-line no-await-in-loop
          await markExternalSessionCompleted(
            attended.sessionId,
            attended.session,
            setCookie,
          );
        }
        return 'COMPLETED';
      }

      const code = response.status === 409 && conflictCode(response.error);

      if (code === TASK_ALREADY_COMPLETED) {
        alreadyCompleted = true;
        continue;
      }

      if (code === COMPLETION_REFUSED) {
        refused = true;
        continue;
      }

      console.error(
        'External study completion failed:',
        response.status,
        session.projectId,
      );
      return 'FAILED';
    }

    if (alreadyCompleted) return 'COMPLETED';
    return refused ? 'FAILED' : 'NONE';
  });
